import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {SlotCard} from './slot-card';
import {Markup} from '@amblique/base/app/components/shared/markup';

import {applyKnownBlockInfoTranslations, wrapBlockInfoText} from '../../../utils/wrap-block-info-text';

/**
 * Multi-slot option step: renders a SlotCard for each slot in the container.
 * State-only — product list appears in a sub-view when the parent opens it.
 */
export function OptionStep({
  blockInfo,
  container,
  customizeStepKind = 'other',
  isSimplePDP = false,
  isStepDisabled,
  onSlotChoose,
  registerChooseButtonRef,
  selections = [],
  styles: stylesProp,
}) {
  const intl = useIntl();
  const themeStyles = useMultiStyleConfig('ConfigurationSelector', {});
  const styles = stylesProp || themeStyles;

  if (!container || !container.values || container.values.length === 0) {
    return null;
  }

  return (
    <Box
      className={`option-step-container customize-steps-option-step customize-steps-option-step--${customizeStepKind}`}
      data-customize-step-kind={customizeStepKind}
    >
      {blockInfo && (
        <Box className="step-information customize-steps-step-information" {...styles.stepDescription}>
          <Box {...styles.stepDescriptionBody}>
            <Markup source={wrapBlockInfoText(applyKnownBlockInfoTranslations(blockInfo, intl))} />
          </Box>
        </Box>
      )}
      {container.values.map((value, slotIndex) => {
        // Hidden slots are auto-selected in background; not shown to the user
        if (value.isVisible === false) return null;

        const selection = selections[slotIndex];
        const slotLabel = value.typeName || `Slot ${slotIndex + 1}`;

        return (
          <SlotCard
            key={slotIndex}
            buttonRef={registerChooseButtonRef ? (el) => registerChooseButtonRef(slotIndex, el) : undefined}
            isDisabled={isStepDisabled}
            isSimplePDP={isSimplePDP}
            label={slotLabel}
            selection={selection}
            slotIndex={slotIndex}
            stepKind={customizeStepKind}
            styles={styles}
            value={value}
            onChoose={() => onSlotChoose(slotIndex)}
          />
        );
      })}
    </Box>
  );
}

OptionStep.displayName = 'OptionStep';

OptionStep.propTypes = {
  blockInfo: PropTypes.string,
  customizeStepKind: PropTypes.string,
  isSimplePDP: PropTypes.bool,
  container: PropTypes.shape({
    containerId: PropTypes.string.isRequired,
    containerName: PropTypes.string,
    isVisible: PropTypes.bool,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        isRequired: PropTypes.bool,
        isVisible: PropTypes.bool,
        products: PropTypes.arrayOf(
          PropTypes.shape({
            isHidden: PropTypes.bool,
            isPreselected: PropTypes.bool,
            productId: PropTypes.string.isRequired,
          })
        ),
        recommendationTypeId: PropTypes.number.isRequired,
        slotLabel: PropTypes.string,
        typeName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  isStepDisabled: PropTypes.bool,
  onSlotChoose: PropTypes.func.isRequired,
  registerChooseButtonRef: PropTypes.func,
  selections: PropTypes.array,
  styles: PropTypes.object,
};
